import React, { useEffect, useState, useCallback, useMemo } from "react";
import { fetchItinerary } from "../../api";
import { useBreakpointsContext, useScroll } from "../../hooks";
import Itinerary from "./Itinerary";
import { connect } from "../../store";
import { useWindowHeight } from "../planner/Planner";
import { getStops } from "../layout/utils";
import {
    MAP_HEIGHT_MOBILE,
    MAP_HEADER_HEIGHT_MOBILE,
    ITINERARY_HEADER_HEIGHT,
    ITINERARY_HEADER_HEIGHT_MOBILE,
    DAY_GROUP_HEADER_HEIGHT,
    DAY_GROUP_HEADER_HEIGHT_MOBILE,
    APP_HEADER,
    APP_HEADER_MOBILE,
} from "../../constants";
import { useStopsById } from "../route-explorer/hooks";
import { trackStopInView } from "../shared/AnalyticsTracker";

const useItineraryFetch = itineraryId => {
    const [itinerary, setItinerary] = useState();
    useEffect(() => {
        const callback = async () => {
            if (!itineraryId) {
                return;
            }
            try {
                const itinerary = await fetchItinerary(itineraryId);
                setItinerary(itinerary);
            } catch (e) {
                return;
            }
        };
        callback();
    }, [itineraryId]);
    return itinerary;
};
export const getItineraryCardsTopOffset = ({
    useOldLayout = true,
    useTwoColumnLayout = false,
    isMobile,
    isTablet,
    windowHeight,
    itinerary,
    inPlanner = false,
    sizeByStickyContent = null,
}) => {
    if (!itinerary) {
        return 0;
    }
    const itineraryMetadata = itinerary?.itineraryData?.metadata;
    const isShowOptionsEnabled = false && itineraryMetadata.customizations === "active";
    const appHeaderHeight = isMobile || isTablet ? APP_HEADER_MOBILE : APP_HEADER;
    let stickyContentHeight;
    if (useOldLayout) {
        if (isMobile) {
            stickyContentHeight =
                appHeaderHeight +
                ITINERARY_HEADER_HEIGHT_MOBILE +
                MAP_HEIGHT_MOBILE +
                (inPlanner ? 0 : MAP_HEADER_HEIGHT_MOBILE) +
                DAY_GROUP_HEADER_HEIGHT_MOBILE;
        } else if (isTablet) {
            stickyContentHeight =
                appHeaderHeight +
                (inPlanner ? 0 : ITINERARY_HEADER_HEIGHT) +
                DAY_GROUP_HEADER_HEIGHT +
                MAP_HEIGHT_MOBILE +
                (inPlanner ? 0 : MAP_HEADER_HEIGHT_MOBILE);
        } else {
            stickyContentHeight =
                appHeaderHeight +
                (inPlanner && !isShowOptionsEnabled ? 0 : ITINERARY_HEADER_HEIGHT) +
                DAY_GROUP_HEADER_HEIGHT;
        }
    } else {
        if (useTwoColumnLayout) {
            stickyContentHeight =
                appHeaderHeight +
                (inPlanner && !isShowOptionsEnabled ? 0 : ITINERARY_HEADER_HEIGHT) +
                DAY_GROUP_HEADER_HEIGHT;
        } else if (inPlanner && sizeByStickyContent) {
            stickyContentHeight =
                appHeaderHeight + sizeByStickyContent.dayComputedHeight + sizeByStickyContent.stickyMapComputedHeight;
            return stickyContentHeight;
        } else {
            stickyContentHeight =
                appHeaderHeight +
                ITINERARY_HEADER_HEIGHT_MOBILE +
                MAP_HEIGHT_MOBILE +
                (inPlanner ? 0 : MAP_HEADER_HEIGHT_MOBILE) +
                DAY_GROUP_HEADER_HEIGHT_MOBILE;
        }
    }
    return stickyContentHeight + (windowHeight - stickyContentHeight) / 4;
};

function ItineraryContainer(props) {
    const {
        itineraryId,
        onFetchItinerary,
        placeEndNodeByPlaceId,
        onActiveStopIdChange,
        onIsSecondWayVisibleChange,
        onIsMetadataInScrollViewChange,
        onActivePlaceStartIdChange,
        activeStopId,
        isMetadataInScrollView,
        activePlaceStartId,
        itineraryMetadataClassName,
        itineraryItemsClassName,
        renderMap,
        loopDirection,
        setLoopDirection,
        loaderClassName,
        dayTopOffset,
        inPlanner,
        enableChangeItinerary,
        onItineraryIdChange,
        onStartingPointChange,
        routeItineraries,
        inLauncherModal,
        useTwoColumnLayout,
        useOldLayout,
        sizeByStickyContent,
        showToast,
        setActiveSuggestionId,
        activeSuggestionId,
    } = props;
    const itinerary = useItineraryFetch(itineraryId);
    const { isMobile, isTablet } = useBreakpointsContext();
    const windowHeight = useWindowHeight();
    const cardsOffsetTop = getItineraryCardsTopOffset({
        useOldLayout,
        useTwoColumnLayout,
        isMobile,
        isTablet,
        windowHeight,
        itinerary,
        inPlanner,
        sizeByStickyContent,
    });
    const [startingPoint, setStartingPoint] = useState(null);
    const [formattedStops, setFormattedStops] = useState([]);
    const [isOpenItinerarySwitcher, setIsOpenItinerarySwitcher] = useState(false);
    const stopsById = useStopsById(itinerary);
    const [activeDayId, onActiveDayIdChange] = useState(null);
    const [itineraryItemsPaddingBottom, setItineraryItemsPaddingBottom] = useState(null);
    const lastStopId = useMemo(() => {
        const stops = formattedStops;

        if (!stops || !stops.length) {
            return;
        }
        return stops[stops.length - 1]?.id;
    }, [formattedStops]);

    useEffect(() => {
        onStartingPointChange(startingPoint);
    }, [startingPoint, onStartingPointChange]);
    useEffect(() => {
        if (!itinerary) {
            return;
        }
        setFormattedStops(getStops(itinerary, loopDirection, itineraryId, startingPoint?.coordinates));
    }, [itinerary, itineraryId, loopDirection, startingPoint]);

    useEffect(() => {
        onFetchItinerary(itinerary);
        return () => onFetchItinerary(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itinerary]);

    //For tracking active stop in analytics
    useEffect(() => {
        if (activeStopId) {
            trackStopInView("tripper.app", stopsById[activeStopId].en.name);
        }
    }, [activeStopId]);

    useScroll(
        useCallback(() => {
            const itineraryMetadataEl = document.querySelector(".js__itinerary-metadata");
            if (itineraryMetadataEl) {
                const { y: nodeY, height: cardHeight } = itineraryMetadataEl.getBoundingClientRect();
                const scrollPosition = window.scrollY;

                if (scrollPosition < 200 || (nodeY < cardsOffsetTop && nodeY > cardsOffsetTop - cardHeight)) {
                    //Metadata card is in scroll view
                    onIsMetadataInScrollViewChange(true);
                } else if (isMetadataInScrollView) {
                    onIsMetadataInScrollViewChange(false);
                }
            }

            //Change ItineraryDay bg color when scrolling
            document.querySelectorAll(".js__itinerary-day").forEach(dayEl => {
                const dayId = dayEl.dataset.dayId;
                const { y: nodeY, height } = dayEl.getBoundingClientRect();
                const offset = cardsOffsetTop - (useOldLayout || useTwoColumnLayout ? 0 : height);

                if (nodeY <= offset) {
                    //Stop card is in scroll view
                    onActiveDayIdChange(dayId);
                } else if (activeDayId === dayId) {
                    onActiveDayIdChange(null);
                }
            });

            document.querySelectorAll(".js__stop-card").forEach(cardEl => {
                const stopId = cardEl.dataset.stopId;
                const isMidwayPoint = stopsById[stopId].isMidwayPoint;
                const { y: nodeY, height: cardHeight } = cardEl.getBoundingClientRect();
                const cardPaddingTop = parseInt(window.getComputedStyle(cardEl).paddingTop);
                const offsetTop =
                    cardsOffsetTop + (useOldLayout || useTwoColumnLayout || cardPaddingTop === 20 ? 0 : 20);

                if (nodeY <= offsetTop && nodeY >= offsetTop - cardHeight) {
                    //Stop card is in scroll view
                    onActiveStopIdChange(stopId);
                } else if (activeStopId === stopId) {
                    onActiveStopIdChange(null);
                }

                if (isMidwayPoint) {
                    if (nodeY <= offsetTop) {
                        onIsSecondWayVisibleChange(true);
                    } else {
                        onIsSecondWayVisibleChange(false);
                    }
                }

                //Update stop list padding bottom
                if (stopId === lastStopId) {
                    if (useTwoColumnLayout && nodeY < cardsOffsetTop) {
                        //Scroll position is below stop card top
                        //Last stop card reached
                        //In desktop
                        const footerEl = document.querySelector(".js__itinerary-footer");
                        const itineraryListEl = document.querySelector(".js__itinerary-items");
                        if (!footerEl || !itineraryListEl) {
                            return;
                        }
                        const footerBottom = footerEl.getBoundingClientRect().bottom;

                        //Calculate stop list padding bottom
                        const offset = 100; //Prevent infinite scroll jump
                        const paddingBottom =
                            footerBottom < windowHeight
                                ? offset + windowHeight - (footerBottom - nodeY) - cardsOffsetTop
                                : 0;
                        setItineraryItemsPaddingBottom(paddingBottom < 0 ? 0 : paddingBottom);
                    } else {
                        //By default, padding bottom is managed by CSS
                        setItineraryItemsPaddingBottom(null);
                    }
                }
            });

            document.querySelectorAll(".js__place-start-card").forEach(placeStartNode => {
                const placeStartId = placeStartNode.dataset.placeStartId;
                //console.log(placeStartId);
                const placeEndNode = placeEndNodeByPlaceId[placeStartId];
                const { y: startNodeY, height: startNodeHeight } = placeStartNode.getBoundingClientRect();

                if (!placeEndNode) {
                    return;
                }

                const placeStartPaddingTop = parseInt(window.getComputedStyle(placeStartNode).paddingTop);
                const offsetTop =
                    cardsOffsetTop + (useOldLayout || useTwoColumnLayout || placeStartPaddingTop === 20 ? 0 : 20);
                const { y: endNodeY, height: endNodeHeight } = placeEndNode.getBoundingClientRect();

                if (startNodeY < offsetTop && startNodeY > offsetTop - startNodeHeight) {
                    //Place start card is in scroll view
                    onActiveStopIdChange(placeStartId);
                } else if (activeStopId === placeStartId) {
                    onActiveStopIdChange(null);
                }

                if (startNodeY < offsetTop && endNodeY > offsetTop - endNodeHeight) {
                    //Place start card and its stops cards are in scroll view
                    onActivePlaceStartIdChange(placeStartId);
                } else if (activePlaceStartId === placeStartId) {
                    onActivePlaceStartIdChange(null);
                }
            });

            document.querySelectorAll(".js__suggestion-card").forEach(cardEl => {
                const stopId = cardEl.dataset.stopId;
                const { y: nodeY, height: cardHeight } = cardEl.getBoundingClientRect();
                const cardPaddingTop = 0;
                const offsetTop =
                    cardsOffsetTop + (useOldLayout || useTwoColumnLayout || cardPaddingTop === 20 ? 0 : 20);

                if (nodeY <= offsetTop && nodeY >= offsetTop - cardHeight) {
                    //Stop card is in scroll view
                    setActiveSuggestionId(stopId);
                } else if (activeSuggestionId === stopId) {
                    setActiveSuggestionId(null);
                }
            });
        }, [
            useTwoColumnLayout,
            lastStopId,
            windowHeight,
            activeDayId,
            stopsById,
            onIsMetadataInScrollViewChange,
            isMetadataInScrollView,
            onIsSecondWayVisibleChange,
            onActiveStopIdChange,
            onActivePlaceStartIdChange,
            activePlaceStartId,
            placeEndNodeByPlaceId,
            cardsOffsetTop,
            activeStopId,
            useOldLayout,
            setActiveSuggestionId,
            activeSuggestionId,
        ]),
        100,
    );

    return (
        <Itinerary
            startingPoint={startingPoint}
            setStartingPoint={setStartingPoint}
            data={itinerary}
            itineraryId={itineraryId}
            activeStopId={activeStopId}
            itineraryMetadataClassName={itineraryMetadataClassName}
            itineraryItemsClassName={itineraryItemsClassName}
            loaderClassName={loaderClassName}
            renderMap={renderMap}
            formattedStops={formattedStops}
            setLoopDirection={setLoopDirection}
            loopDirection={loopDirection}
            dayTopOffset={dayTopOffset}
            activeDayId={activeDayId}
            itineraryItemsPaddingBottom={itineraryItemsPaddingBottom}
            enableChangeItinerary={enableChangeItinerary}
            onItineraryIdChange={onItineraryIdChange}
            routeItineraries={routeItineraries}
            setIsOpenItinerarySwitcher={setIsOpenItinerarySwitcher}
            isOpenItinerarySwitcher={isOpenItinerarySwitcher}
            inLauncherModal={inLauncherModal}
            inPlanner={inPlanner}
            useTwoColumnLayout={useTwoColumnLayout}
            useOldLayout={useOldLayout}
            cardsOffsetTop={cardsOffsetTop}
            showToast={showToast}
            activeSuggestionId={activeSuggestionId}
        />
    );
}

ItineraryContainer.defaultProps = {
    useOldLayout: true,
    loopDirection: "CLOCKWISE",
    onStartingPointChange: () => {},
    setActiveSuggestionId: () => {},
    activeSuggestionId: null,
};

const mapStateToProps = ({ placeEndNodeByPlaceId }) => ({
    placeEndNodeByPlaceId,
});

export default connect(mapStateToProps)(ItineraryContainer);
